import { useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import { api } from "../config/api";
import { useMyContext } from "../context/MyContext";
import { toast } from "react-toastify";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "../config/firebase";
import { LockOutlined } from '@ant-design/icons';

function ChangePasswordModal({ visible, onCancel }) {
  const [loading, setLoading] = useState(false);
  const { user, logout } = useMyContext();
  const [form] = Form.useForm();

  // Function to verify current password with Firebase
  const verifyCurrentPassword = async (password) => {
    try {
      // console.log("Verifying current password...");
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // console.log("Current password verification successful");
      return true;
    } catch (error) {
      console.error("Current password verification failed:", error);
      return false;
    }
  };

  const handleSave = async (values) => {
    // console.log("Starting password change process...");
    setLoading(true);
    try {
      // First verify the current password
      const isCurrentPasswordValid = await verifyCurrentPassword(values.currentPassword);
      if (!isCurrentPasswordValid) {
        toast.error("Current password is incorrect!");
        setLoading(false);
        return;
      }

      // Check if new password is same as current password
      if (values.currentPassword === values.password) {
        // console.log("New password matches current password - rejecting");
        toast.error("New password must be different from your current password!");
        setLoading(false);
        return;
      }

      // console.log("Submitting password change to backend...");
      await api.put(`/users/${user?.uid}/secure`, {
        password: values.password,
      });

      // console.log("Password change successful");
      toast.success("Password changed successfully!");
      toast.success("Try log in with New Password!");
      form.resetFields();
      
      // Close the modal first, then logout 
      onCancel();
      
      // Use setTimeout to ensure modal is closed before logout
      setTimeout(() => {
        logout();
      }, 300);
      
    } catch (error) {
      console.error("Password change failed:", error);
      toast.error(error?.response?.data?.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <LockOutlined className="text-blue-500" />
          <span>Change Password</span>
        </div>
      }
      open={visible}
      footer={null}
      onCancel={onCancel}
      closable={false} // Prevent closing with X button
      maskClosable={false} // Prevent closing by clicking outside
    >
      <Form layout="vertical" onFinish={handleSave} form={form}>
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[
            {
              required: true,
              message: "Please enter your current password!",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter current password"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="New password"
          rules={[
            {
              required: true,
              message: "Please enter new password!",
            },
            {
              min: 6,
              message: "Password should be at least 6 characters long!",
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
              message:
                "Password must contain at least one uppercase letter, one lowercase letter, one number, and one symbol.",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter new password"
          />
        </Form.Item>

        <Form.Item
          name="confirm"
          dependencies={["password"]}
          hasFeedback
          label="Confirm password"
          rules={[
            {
              required: true,
              message: "Please confirm your password!",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The passwords that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Confirm password"
          />
        </Form.Item>

        <Form.Item className="flex items-center justify-center">
          <Button
            type="primary"
            className="text-white"
            loading={loading}
            htmlType="submit"
          >
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ChangePasswordModal;